import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { validateInvite, forgotPassword, resetPassword, verifyResetToken } from '../services/auth';
import { AlertCircle, ArrowLeft, CheckCircle, Eye, EyeOff, Gift, KeyRound, LogIn, Rocket, Shield, TrendingUp, UserPlus, Zap } from 'lucide-react';

// Subtle Gradient Pulse — CSS-only, lightweight & professional
const GradientPulseBackground = () => {
  const keyframes = `
    @keyframes pulseOrb1 {
      0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.15; }
      50% { transform: translate(30px, -20px) scale(1.15); opacity: 0.25; }
    }
    @keyframes pulseOrb2 {
      0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.10; }
      50% { transform: translate(-25px, 15px) scale(1.1); opacity: 0.18; }
    }
    @keyframes pulseOrb3 {
      0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.08; }
      50% { transform: translate(15px, 25px) scale(1.2); opacity: 0.14; }
    }
    @keyframes shimmerLine {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
  `;

  return (
    <>
      <style>{keyframes}</style>
      <div style={{
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        zIndex: 0, overflow: 'hidden', background: '#FFFFFF',
      }}>
        {/* Subtle maroon gradient accent */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse at 50% 0%, rgba(139, 21, 56, 0.06) 0%, transparent 60%)',
        }} />

        {/* Orb 1 — soft maroon, top-right */}
        <div style={{
          position: 'absolute', top: '-15%', right: '-10%',
          width: '600px', height: '600px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139, 21, 56, 0.08) 0%, transparent 70%)',
          filter: 'blur(80px)',
          animation: 'pulseOrb1 8s ease-in-out infinite',
        }} />

        {/* Orb 2 — soft warm, bottom-left */}
        <div style={{
          position: 'absolute', bottom: '-20%', left: '-5%',
          width: '500px', height: '500px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139, 21, 56, 0.06) 0%, transparent 70%)',
          filter: 'blur(90px)',
          animation: 'pulseOrb2 10s ease-in-out infinite',
        }} />
      </div>
    </>
  );
};

const AuthPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isBM = language === 'bm' || language === 'ms';
  
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Forgot/Reset Password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [resetEmail, setResetEmail] = useState('');
  
  // Invite system state
  const [inviteCode, setInviteCode] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [inviteError, setInviteError] = useState('');
  const [validatingInvite, setValidatingInvite] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    newPassword: '',
    confirmPassword: ''
  });

  const { login, register, registerWithInvite } = useAuth();

  // Check for invite code or reset token in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const invite = urlParams.get('invite');
    const token = urlParams.get('token');
    
    if (invite) {
      setInviteCode(invite);
      setIsLogin(false); // Switch to register mode
      validateInviteCode(invite);
    }
    
    // Check for password reset token
    if (token && window.location.pathname.includes('reset-password')) {
      setResetToken(token);
      setShowResetPassword(true);
      verifyResetTokenHandler(token);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateInviteCode = async (code) => {
    setValidatingInvite(true);
    setInviteError('');
    try {
      const result = await validateInvite(code);
      if (result.valid) {
        setInviteData(result);
      } else {
        setInviteError(isBM ? 'Invite link is invalid or has expired' : 'Invite link is invalid or has expired');
        setInviteCode(null);
      }
    } catch (err) {
      const message = err.response?.data?.detail || (isBM ? 'Invalid invite link' : 'Invalid invite link');
      setInviteError(message);
      setInviteCode(null);
    } finally {
      setValidatingInvite(false);
    }
  };

  const verifyResetTokenHandler = async (token) => {
    try {
      const result = await verifyResetToken(token);
      if (result.valid) {
        setResetEmail(result.email);
      }
    } catch (err) {
      setError(isBM ? 'Reset link is invalid or has expired. Please request a new link.' : 'Reset link is invalid or has expired. Please request a new link.');
      setShowResetPassword(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await forgotPassword(formData.email);
      setSuccess(result.message || (isBM ? 'If email is registered, you will receive a password reset link.' : 'If email is registered, you will receive a password reset link.'));
      setFormData({ ...formData, email: '' });
    } catch (err) {
      setError(err.response?.data?.detail || (isBM ? 'An error occurred. Please try again.' : 'An error occurred. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError(isBM ? 'Kata sandi tidak cocok' : 'Passwords do not match');
      return;
    }

    // Validate password strength
    const passwordErrors = [];
    if (formData.newPassword.length < 8) passwordErrors.push(isBM ? 'minimum 8 characters' : 'minimum 8 characters');
    if (!/[A-Z]/.test(formData.newPassword)) passwordErrors.push(isBM ? '1 uppercase letter' : '1 uppercase letter');
    if (!/[a-z]/.test(formData.newPassword)) passwordErrors.push(isBM ? '1 lowercase letter' : '1 lowercase letter');
    if (!/[0-9]/.test(formData.newPassword)) passwordErrors.push(isBM ? '1 number' : '1 number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword)) passwordErrors.push(isBM ? '1 symbol' : '1 symbol');

    if (passwordErrors.length > 0) {
      setError((isBM ? 'Kata sandi harus mengandung: ' : 'Password must contain: ') + passwordErrors.join(', '));
      return;
    }

    setLoading(true);

    try {
      const result = await resetPassword(resetToken, formData.newPassword);
      setSuccess(result.message || (isBM ? 'Kata sandi berhasil diperbarui!' : 'Password successfully updated!'));
      // Redirect to login after 2 seconds
      setTimeout(() => {
        setShowResetPassword(false);
        setResetToken(null);
        setIsLogin(true);
        window.history.pushState({}, '', '/');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || (isBM ? 'An error occurred. Please try again.' : 'An error occurred. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (!result.success) {
          setError(result.error);
        } else {
          // Redirect to dashboard on successful login
          navigate('/dashboard');
        }
      } else {
        // Password strength validation
        const passwordErrors = [];
        if (formData.password.length < 8) {
          passwordErrors.push(isBM ? 'minimum 8 characters' : 'minimum 8 characters');
        }
        if (!/[A-Z]/.test(formData.password)) {
          passwordErrors.push(isBM ? '1 uppercase letter' : '1 uppercase letter');
        }
        if (!/[a-z]/.test(formData.password)) {
          passwordErrors.push(isBM ? '1 lowercase letter' : '1 lowercase letter');
        }
        if (!/[0-9]/.test(formData.password)) {
          passwordErrors.push(isBM ? '1 number' : '1 number');
        }
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
          passwordErrors.push(isBM ? '1 symbol (!@#$%^&*)' : '1 symbol (!@#$%^&*)');
        }
        
        if (passwordErrors.length > 0) {
          setError((isBM ? 'Kata sandi harus mengandung: ' : 'Password must contain: ') + passwordErrors.join(', '));
          setLoading(false);
          return;
        }
        
        // Registration - INVITE ONLY
        if (!inviteCode || !inviteData) {
          setError(isBM ? 'Invite link required to register. Please get an invite from an existing user.' : 'Invite link required to register. Please get an invite from an existing user.');
          setLoading(false);
          return;
        }
        
        // Register with invite code
        const result = await registerWithInvite(
          inviteCode, 
          formData.email, 
          formData.password, 
          formData.name, 
          formData.phone || null
        );
        if (!result.success) {
          setError(result.error);
        } else {
          // Redirect to dashboard on successful registration
          navigate('/dashboard');
        }
      }
    } catch (err) {
      // Catch any remaining unhandled errors with user-friendly messages
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Server is taking too long to respond. Please try again.');
      } else if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
        setError('Unable to connect to server. Please check your internet connection.');
      } else if (err.response?.status === 429) {
        setError('Too many attempts. Please wait a moment and try again.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: TrendingUp, text: 'Institutional Signals' },
    { icon: Shield, text: 'Bank-Grade Encryption' },
    { icon: Zap, text: 'Volume Profile Analysis' }
  ];

  // GG DESK Maroon Theme - White Background
  const colors = {
    bgPrimary: '#FFFFFF',
    bgCard: '#FFFFFF',
    bgInput: '#F5F5F5',
    accent: '#8B1538',
    accentLight: '#B83A55',
    accentGradient: 'linear-gradient(135deg, #8B1538, #B83A55)',
    textPrimary: '#1A1A1A',
    textSecondary: '#4A4A4A',
    textMuted: '#888888',
    border: '#E0E0E0',
    borderFocus: '#8B1538',
    error: '#DC2626',
    success: '#16A34A',
    invite: '#2563EB',
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <GradientPulseBackground />
      <div style={{
        width: '100%',
        maxWidth: '500px',
        position: 'relative',
        zIndex: 1,
      }}>
        
        {/* Logo and Branding */}
        <div style={{
          textAlign: 'center',
          marginBottom: '32px',
        }}>
          {/* Garuda Logo */}
          <img
            src="https://customer-assets.emergentagent.com/job_a04d7c43-2539-4e75-9e44-31d510c5bd81/artifacts/m56e7v94_IMG_2138.jpeg"
            alt="GG DESK Garuda Logo"
            data-testid="login-garuda-logo"
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              objectFit: 'cover',
              margin: '0 auto 16px',
              display: 'block',
              boxShadow: '0 4px 20px rgba(139, 21, 56, 0.2)',
              border: '3px solid rgba(139, 21, 56, 0.15)',
            }}
          />
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            background: colors.accentGradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: '4px',
          }}>
            GG DESK
          </h1>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '10px',
          }}>
            <div style={{ height: '1px', width: '40px', background: 'linear-gradient(to right, transparent, rgba(0,0,0,0.15))' }} />
            <span style={{
              fontSize: '0.65rem',
              fontWeight: '600',
              letterSpacing: '3px',
              textTransform: 'uppercase',
              color: 'rgba(0,0,0,0.4)',
            }}>
              PREMIUM SIGNAL
            </span>
            <div style={{ height: '1px', width: '40px', background: 'linear-gradient(to left, transparent, rgba(0,0,0,0.15))' }} />
          </div>
          <p style={{ 
            color: colors.accent, 
            fontSize: '0.8rem', 
            fontStyle: 'italic',
            fontWeight: '500',
            letterSpacing: '0.5px',
            marginBottom: '8px'
          }}>
            "Sinyal Cerdas untuk Trader Cerdas."
          </p>
          <p style={{ color: colors.textMuted, fontSize: '0.78rem' }}>
            {isBM ? 'Sinyal Trading AI Premium untuk Trader Profesional' : 'Premium AI Trading Signals for Professional Traders'}
          </p>
        </div>

        {/* Invite Banner */}
        {validatingInvite && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            background: `${colors.invite}15`,
            border: `1px solid ${colors.invite}40`,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: '2px solid',
              borderColor: `${colors.invite} transparent ${colors.invite} transparent`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            <span style={{ color: colors.invite, fontSize: '0.9rem' }}>
              {isBM ? 'Verifying invite link...' : 'Verifying invite link...'}
            </span>
          </div>
        )}

        {inviteData && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            background: `${colors.success}15`,
            border: `1px solid ${colors.success}40`,
            borderRadius: '12px',
          }} data-testid="invite-valid-banner">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <Gift size={20} style={{ color: colors.success }} />
              <span style={{ color: colors.success, fontWeight: '600', fontSize: '0.95rem' }}>
                {isBM ? 'Valid Invite Link!' : 'Valid Invite Link!'}
              </span>
            </div>
            <p style={{ color: colors.textSecondary, fontSize: '0.85rem', margin: 0 }}>
              {isBM 
                ? <>Selamat datang, <strong>{inviteData.client_name}</strong>! Silakan lengkapkan pendaftaran anda di bawah.</>
                : <>Welcome, <strong>{inviteData.client_name}</strong>! Please complete your registration below.</>}
            </p>
          </div>
        )}

        {inviteError && (
          <div style={{
            marginBottom: '20px',
            padding: '16px',
            background: `${colors.error}15`,
            border: `1px solid ${colors.error}40`,
            borderRadius: '12px',
          }} data-testid="invite-error-banner">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <AlertCircle size={20} style={{ color: colors.error }} />
              <span style={{ color: colors.error, fontSize: '0.9rem' }}>
                {inviteError}
              </span>
            </div>
          </div>
        )}

        {/* Auth Card */}
        <div style={{
          background: colors.accentGradient,
          borderRadius: '24px',
          padding: '2px',
          boxShadow: '0 4px 30px rgba(139, 21, 56, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08)',
        }}>
          <div style={{
            backgroundColor: '#FFFFFF',
            backdropFilter: 'blur(20px)',
            borderRadius: '22px',
            padding: '32px',
          }}>
            
            {/* Welcome Message */}
            <div style={{
              textAlign: 'center',
              marginBottom: '24px',
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px',
              }}>
                <Rocket style={{ width: '24px', height: '24px', color: colors.accent }} />
                <span style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: '600', 
                  color: colors.textPrimary 
                }}>
                  {inviteData 
                    ? (isBM ? 'Pendaftaran Invite' : 'Invite Registration')
                    : (isLogin 
                      ? (isBM ? 'Selamat Datang!' : 'Welcome Back!') 
                      : (isBM ? 'Bergabung Sekarang!' : 'Join Us!'))}
                </span>
              </div>
              <p style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                {inviteData 
                  ? (isBM ? 'Lengkapi informasi anda untuk mendaftar' : 'Complete your information to register your account')
                  : (isLogin 
                    ? (isBM ? 'Login untuk akses dashboard trading anda' : 'Login to access your trading dashboard')
                    : (isBM ? 'Buat akun untuk mulai trading cerdas' : 'Register an account to start trading wisely'))}
              </p>
            </div>

            {/* Tab Switcher - Hide when invite is active */}
            {!inviteData && (
              <>
              <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '24px',
                backgroundColor: colors.bgInput,
                padding: '4px',
                borderRadius: '12px',
              }}>
                <button
                  onClick={() => { setIsLogin(true); setError(''); }}
                  data-testid="login-tab"
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    border: 'none',
                    cursor: 'pointer',
                    background: isLogin ? colors.accentGradient : 'transparent',
                    color: isLogin ? '#FFFFFF' : colors.textMuted,
                    fontWeight: isLogin ? '600' : '400',
                    fontSize: '0.9375rem',
                  }}
                >
                  <LogIn style={{ width: '16px', height: '16px' }} />
                  {isBM ? 'Masuk' : 'Login'}
                </button>
                <button
                  onClick={() => { setIsLogin(false); setError(''); }}
                  data-testid="register-tab"
                  disabled={!inviteCode}
                  title={!inviteCode ? (isBM ? 'PM Admin untuk dapatkan invite link' : 'PM Admin to get invite link') : ''}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    border: 'none',
                    cursor: !inviteCode ? 'not-allowed' : 'pointer',
                    background: !isLogin ? colors.accentGradient : 'transparent',
                    color: !isLogin ? '#FFFFFF' : colors.textMuted,
                    fontWeight: !isLogin ? '600' : '400',
                    fontSize: '0.9375rem',
                    opacity: !inviteCode ? 0.5 : 1,
                  }}
                >
                  <UserPlus style={{ width: '16px', height: '16px' }} />
                  {isBM ? 'Daftar' : 'Register'}
                </button>
              </div>
              
              {/* Hint for disabled register button */}
              {!inviteCode && isLogin && (
                <div style={{
                  textAlign: 'center',
                  fontSize: '11px',
                  color: '#F59E0B',
                  marginBottom: '16px',
                  padding: '8px 12px',
                  background: 'rgba(245, 158, 11, 0.1)',
                  borderRadius: '8px'
                }}>
                  🔒 {isBM ? 'Pendaftaran memerlukan invite link. Hubungi Admin.' : 'Registration requires invite link. PM Admin for access.'}
                </div>
              )}
            </>
            )}

            {/* Invite-Only Notice */}
            {!isLogin && !inviteCode && (
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: 'rgba(139, 21, 56, 0.06)',
                border: '1px solid rgba(139, 21, 56, 0.15)',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔒</div>
                <div style={{ color: colors.accent, fontWeight: '600', marginBottom: '8px' }}>
                  {isBM ? 'Pendaftaran Eksklusif' : 'Exclusive Registration'}
                </div>
                <div style={{ color: colors.textMuted, fontSize: '13px', lineHeight: '1.6' }}>
                  {isBM 
                    ? <>Pendaftaran hanya bisa dilakukan melalui <strong style={{ color: '#1A1A1A' }}>Invite Link</strong> yang diverifikasi oleh admin.</>
                    : <>Registration can only be done through <strong style={{ color: '#1A1A1A' }}>Invite Link</strong> verified by admin.</>}
                </div>
                <div style={{ 
                  marginTop: '12px', 
                  padding: '12px',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px',
                  fontSize: '12px', 
                  color: '#F59E0B',
                  lineHeight: '1.5'
                }}>
                  📩 <strong>{isBM ? 'Cara Mendaftar:' : 'How to Register:'}</strong><br />
                  {isBM ? 'Hubungi Admin untuk dapatkan invite link dan akses platform.' : 'PM Admin to get invite link and platform access.'}
                </div>
                <div style={{ marginTop: '12px', fontSize: '12px', color: colors.textMuted }}>
                  {isBM ? 'Sudah punya akun? ' : 'Already have an account? '}
                  <span 
                    onClick={() => setIsLogin(true)} 
                    style={{ color: colors.accent, cursor: 'pointer', textDecoration: 'underline' }}
                  >{isBM ? 'Masuk di sini' : 'Login here'}</span>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div style={{
                marginBottom: '20px',
                padding: '14px',
                backgroundColor: 'rgba(255, 82, 82, 0.1)',
                border: '1px solid rgba(255, 82, 82, 0.3)',
                borderRadius: '10px',
                color: colors.error,
                fontSize: '0.875rem',
              }} data-testid="auth-error">
                {error}
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div style={{
                marginBottom: '20px',
                padding: '14px',
                backgroundColor: 'rgba(105, 240, 174, 0.1)',
                border: '1px solid rgba(105, 240, 174, 0.3)',
                borderRadius: '10px',
                color: colors.success,
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }} data-testid="auth-success">
                <CheckCircle size={18} />
                {success}
              </div>
            )}

            {/* FORGOT PASSWORD FORM */}
            {showForgotPassword && !showResetPassword && (
              <div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px', 
                  marginBottom: '20px',
                  cursor: 'pointer'
                }} onClick={() => { setShowForgotPassword(false); setError(''); setSuccess(''); }}>
                  <ArrowLeft size={18} style={{ color: colors.accent }} />
                  <span style={{ color: colors.accent, fontSize: '0.9rem' }}>{isBM ? 'Back to Login' : 'Back to Login'}</span>
                </div>
                
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <KeyRound size={40} style={{ color: colors.accent, marginBottom: '12px' }} />
                  <h3 style={{ color: colors.textPrimary, margin: '0 0 8px', fontSize: '1.25rem' }}>
                    {isBM ? 'Lupa Kata Sandi?' : 'Forgot Password?'}
                  </h3>
                  <p style={{ color: colors.textMuted, fontSize: '0.85rem', margin: 0 }}>
                    {isBM ? 'Masukkan email anda dan kami akan kirim link untuk reset kata sandi.' : 'Enter your email and we will send a password reset link.'}
                  </p>
                </div>

                <form onSubmit={handleForgotPassword} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      color: colors.textSecondary,
                      fontSize: '0.875rem',
                      marginBottom: '8px',
                      fontWeight: '500',
                    }}>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        backgroundColor: colors.bgInput,
                        border: `2px solid ${colors.border}`,
                        borderRadius: '12px',
                        color: colors.textPrimary,
                        fontSize: '1rem',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                      placeholder="email@example.com"
                      data-testid="forgot-email"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="forgot-submit"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: colors.accentGradient,
                      color: '#FFFFFF',
                      fontWeight: '600',
                      fontSize: '1rem',
                      borderRadius: '12px',
                      border: 'none',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 15px rgba(128, 0, 32, 0.4)',
                    }}
                  >
                    {loading ? (isBM ? 'Mengirim...' : 'Sending...') : (isBM ? 'Send Reset Link' : 'Send Reset Link')}
                  </button>
                </form>
              </div>
            )}

            {/* RESET PASSWORD FORM */}
            {showResetPassword && (
              <div>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <KeyRound size={40} style={{ color: colors.accent, marginBottom: '12px' }} />
                  <h3 style={{ color: colors.textPrimary, margin: '0 0 8px', fontSize: '1.25rem' }}>
                    {isBM ? 'Reset Kata Sandi' : 'Reset Password'}
                  </h3>
                  <p style={{ color: colors.textMuted, fontSize: '0.85rem', margin: 0 }}>
                    {resetEmail && `Email: ${resetEmail}`}<br />
                    {isBM ? 'Masukkan kata sandi baru anda.' : 'Enter your new password.'}
                  </p>
                </div>

                <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      color: colors.textSecondary,
                      fontSize: '0.875rem',
                      marginBottom: '8px',
                      fontWeight: '500',
                    }}>{isBM ? 'Kata Sandi Baru' : 'New Password'}</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.newPassword}
                        onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                        required
                        style={{
                          width: '100%',
                          padding: '14px 50px 14px 16px',
                          backgroundColor: colors.bgInput,
                          border: `2px solid ${colors.border}`,
                          borderRadius: '12px',
                          color: colors.textPrimary,
                          fontSize: '1rem',
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                        placeholder={isBM ? "Kata laluan baru" : "New password"}
                        data-testid="reset-new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: 'absolute',
                          right: '14px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: colors.textMuted,
                          cursor: 'pointer',
                          padding: '4px',
                        }}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '6px' }}>
                      {isBM ? 'Min 8 aksara, 1 uppercase letter, 1 lowercase letter, 1 number, 1 symbol' : 'Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 symbol'}
                    </div>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      color: colors.textSecondary,
                      fontSize: '0.875rem',
                      marginBottom: '8px',
                      fontWeight: '500',
                    }}>{isBM ? 'Konfirmasi Kata Sandi' : 'Confirm Password'}</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        backgroundColor: colors.bgInput,
                        border: `2px solid ${colors.border}`,
                        borderRadius: '12px',
                        color: colors.textPrimary,
                        fontSize: '1rem',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                      placeholder={isBM ? "Konfirmasi kata sandi" : "Confirm password"}
                      data-testid="reset-confirm-password"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="reset-submit"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: colors.accentGradient,
                      color: '#FFFFFF',
                      fontWeight: '600',
                      fontSize: '1rem',
                      borderRadius: '12px',
                      border: 'none',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 15px rgba(128, 0, 32, 0.4)',
                    }}
                  >
                    {loading ? (isBM ? 'Memperbarui...' : 'Updating...') : (isBM ? 'Reset Kata Sandi' : 'Reset Password')}
                  </button>
                </form>
              </div>
            )}

            {/* Form - Only show when not in forgot/reset mode */}
            {!showForgotPassword && !showResetPassword && (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {(!isLogin || inviteData) && (
                <div>
                  <label style={{
                    display: 'block',
                    color: colors.textSecondary,
                    fontSize: '0.875rem',
                    marginBottom: '8px',
                    fontWeight: '500',
                  }}>{isBM ? 'Nama' : 'Name'}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      backgroundColor: colors.bgInput,
                      border: `2px solid ${colors.border}`,
                      borderRadius: '12px',
                      color: colors.textPrimary,
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                    }}
                    placeholder={isBM ? "Nama penuh anda" : "Your full name"}
                    required={!isLogin || !!inviteData}
                    data-testid="register-name"
                    onFocus={(e) => e.target.style.borderColor = colors.borderFocus}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                  />
                </div>
              )}

              <div>
                <label style={{
                  display: 'block',
                  color: colors.textSecondary,
                  fontSize: '0.875rem',
                  marginBottom: '8px',
                  fontWeight: '500',
                }}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    backgroundColor: colors.bgInput,
                    border: `2px solid ${colors.border}`,
                    borderRadius: '12px',
                    color: colors.textPrimary,
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box',
                  }}
                  placeholder="email@example.com"
                  required
                  data-testid="auth-email"
                  onFocus={(e) => e.target.style.borderColor = colors.borderFocus}
                  onBlur={(e) => e.target.style.borderColor = colors.border}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  color: colors.textSecondary,
                  fontSize: '0.875rem',
                  marginBottom: '8px',
                  fontWeight: '500',
                }}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '14px 48px 14px 16px',
                      backgroundColor: colors.bgInput,
                      border: `2px solid ${colors.border}`,
                      borderRadius: '12px',
                      color: colors.textPrimary,
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                    }}
                    placeholder={isLogin && !inviteData ? (isBM ? "Masukkan kata sandi" : "Enter password") : (isBM ? "Min. 8 karakter" : "Min. 8 characters")}
                    required
                    data-testid="auth-password"
                    onFocus={(e) => e.target.style.borderColor = colors.borderFocus}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: colors.textMuted,
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {showPassword ? <EyeOff style={{ width: '20px', height: '20px' }} /> : <Eye style={{ width: '20px', height: '20px' }} />}
                  </button>
                </div>
                
                {/* Password Strength Indicator - Only show on register */}
                {(!isLogin || inviteData) && (
                  <div style={{ marginTop: '10px', padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px', fontWeight: '600' }}>
                      📋 {isBM ? 'Persyaratan Kata Sandi:' : 'Password Requirements:'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {[
                        { check: formData.password.length >= 8, label: isBM ? 'Minimum 8 aksara' : 'Minimum 8 characters' },
                        { check: /[A-Z]/.test(formData.password), label: isBM ? '1 huruf BESAR (A-Z)' : '1 uppercase (A-Z)' },
                        { check: /[a-z]/.test(formData.password), label: isBM ? '1 lowercase letter (a-z)' : '1 lowercase (a-z)' },
                        { check: /[0-9]/.test(formData.password), label: isBM ? '1 number (0-9)' : '1 number (0-9)' },
                        { check: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password), label: isBM ? '1 symbol (!@#$%^&*)' : '1 symbol (!@#$%^&*)' }
                      ].map((item, i) => (
                        <div key={i} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          fontSize: '12px',
                          color: item.check ? '#4ADE80' : (formData.password ? '#FF6B6B' : '#666')
                        }}>
                          <span style={{ width: '16px', textAlign: 'center' }}>
                            {item.check ? '✓' : (formData.password ? '✗' : '○')}
                          </span>
                          <span>{item.label}</span>
                        </div>
                      ))}
                    </div>
                    {formData.password && (
                      <div style={{ 
                        marginTop: '10px', 
                        paddingTop: '10px', 
                        borderTop: '1px solid rgba(255,255,255,0.1)',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {(() => {
                          const checks = [
                            formData.password.length >= 8,
                            /[A-Z]/.test(formData.password),
                            /[a-z]/.test(formData.password),
                            /[0-9]/.test(formData.password),
                            /[!@#$%^&*(),.?":{}|<>]/.test(formData.password)
                          ];
                          const passed = checks.filter(Boolean).length;
                          if (passed === 5) return <span style={{ color: '#4ADE80' }}>✅ {isBM ? 'Kata sandi memenuhi semua persyaratan!' : 'Password meets all requirements!'}</span>;
                          return <span style={{ color: '#FF6B6B' }}>⚠️ {isBM ? `Silakan lengkapkan ${5 - passed} syarat lagi` : `Please complete ${5 - passed} more requirements`}</span>;
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {(!isLogin || inviteData) && (
                <div>
                  <label style={{
                    display: 'block',
                    color: colors.textSecondary,
                    fontSize: '0.875rem',
                    marginBottom: '8px',
                    fontWeight: '500',
                  }}>{isBM ? 'No. Telefon (Opsional)' : 'Phone Number (Optional)'}</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      backgroundColor: colors.bgInput,
                      border: `2px solid ${colors.border}`,
                      borderRadius: '12px',
                      color: colors.textPrimary,
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box',
                    }}
                    placeholder="+62123456789"
                    data-testid="register-phone"
                    onFocus={(e) => e.target.style.borderColor = colors.borderFocus}
                    onBlur={(e) => e.target.style.borderColor = colors.border}
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="auth-submit"
                style={{
                  width: '100%',
                  padding: '16px',
                  background: colors.accentGradient,
                  color: '#FFFFFF',
                  fontWeight: '600',
                  fontSize: '1rem',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  marginTop: '8px',
                  boxShadow: '0 4px 15px rgba(128, 0, 32, 0.4)',
                }}
              >
                {loading ? (
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }} />
                ) : inviteData ? (
                  <>
                    <Gift style={{ width: '20px', height: '20px' }} />
                    {isBM ? 'Register with Invite' : 'Register with Invite'}
                  </>
                ) : isLogin ? (
                  <>
                    <LogIn style={{ width: '20px', height: '20px' }} />
                    {isBM ? 'Masuk' : 'Login'}
                  </>
                ) : (
                  <>
                    <UserPlus style={{ width: '20px', height: '20px' }} />
                    {isBM ? 'Daftar Sekarang' : 'Register Now'}
                  </>
                )}
              </button>

              {/* Forgot Password Link - Only show on login */}
              {isLogin && (
                <div style={{ textAlign: 'center', marginTop: '12px' }}>
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(true); setError(''); setSuccess(''); }}
                    data-testid="forgot-password-link"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: colors.textMuted,
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      padding: 0,
                      textDecoration: 'underline',
                    }}
                  >
                    {isBM ? 'Lupa kata sandi?' : 'Forgot password?'}
                  </button>
                </div>
              )}
            </form>
            )}

            {/* Bottom Text - Hide when invite is active or in forgot/reset mode */}
            {!inviteData && !showForgotPassword && !showResetPassword && (
              <p style={{
                textAlign: 'center',
                color: colors.textMuted,
                fontSize: '0.875rem',
                marginTop: '24px',
              }}>
                {isLogin ? (
                  <>
                    Don't have an account?{' '}
                    <button
                      onClick={() => setIsLogin(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: colors.accent,
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        padding: 0,
                      }}
                    >
                      Register now
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button
                      onClick={() => setIsLogin(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: colors.accent,
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        padding: 0,
                      }}
                    >
                      Login
                    </button>
                  </>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Features */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '24px',
          marginTop: '32px',
          flexWrap: 'wrap',
        }}>
          {features.map((feature, idx) => (
            <div key={idx} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: colors.textMuted,
              fontSize: '0.8rem',
            }}>
              <feature.icon style={{ width: '16px', height: '16px', color: colors.accent }} />
              <span>{feature.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Keyframe animation for spinner */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        input::placeholder {
          color: #666666;
        }
      `}</style>
    </div>
  );
};

export default AuthPage;
